import { useState } from"react";
import {
 Sparkles,
 AlertTriangle,
 ArrowRight,
 Check,
 X,
 Lightbulb,
 ChevronDown,
 ChevronUp,
} from"lucide-react";
import {
 REFORME_TLDR,
 REFORME_POINTS,
 REFORME_TABLE,
 REFORME_ARBRE,
 REFORME_REFLEXES,
 REFORME_QUIZ,
} from"@/data/reforme";

// Rend les codes comptes entre backticks comme badges monospace
function renderText(text) {
 if (!text || typeof text !=="string") return text;
 const parts = text.split(/(`[^`]+`)/g);
 return parts.map((p, i) => {
 if (p.startsWith("`") && p.endsWith("`")) {
 return (
 <code
 key={i}
 className="px-1.5 py-0.5 rounded bg-slate-100 text-[#15294F] font-mono text-[12px] font-bold border border-keyto"
 >
 {p.slice(1, -1)}
 </code>
 );
 }
 return <span key={i}>{p}</span>;
 });
}

function AccountBadge({ value, variant ="default" }) {
 const colors = {
 default:"bg-slate-100 text-[#15294F] border-keyto",
 old:"bg-red-50 text-[#A33B4B] border-[#A33B4B]/30 line-through decoration-2",
 new:"bg-emerald-50 text-[#2F7A5C] border-[#2F7A5C]/30",
 deleted:"bg-slate-100 text-slate-400 border-keyto italic",
 };
 return (
 <span
 className={`inline-block font-mono font-bold text-xs px-2 py-1 rounded border ${colors[variant] || colors.default}`}
 >
 {value}
 </span>
 );
}

function QuizCard({ q, r, idx }) {
 const [open, setOpen] = useState(false);
 return (
 <div
 data-testid={`reforme-quiz-${idx}`}
 className="border border-keyto rounded-xl bg-white overflow-hidden"
 >
 <button
 onClick={() => setOpen((v) => !v)}
 className="w-full px-5 py-4 flex items-start justify-between gap-3 text-left hover:bg-slate-50 transition"
 >
 <div className="flex items-start gap-3 flex-1">
 <span className="shrink-0 w-7 h-7 rounded-full bg-[#15294F] text-white text-xs font-bold flex items-center justify-center">
 {idx + 1}
 </span>
 <p className="text-sm font-semibold text-[#15294F] pt-1">{q}</p>
 </div>
 {open ? (
 <ChevronUp size={16} className="text-slate-400 shrink-0 mt-2" />
 ) : (
 <ChevronDown size={16} className="text-slate-400 shrink-0 mt-2" />
 )}
 </button>
 {open && (
 <div className="px-5 pb-5 pl-[60px]">
 <div className="bg-blue-50/60 border-l-4 border-[#2D6BE0] rounded-r-lg p-4 text-sm text-[#15294F] leading-relaxed">
 {renderText(r)}
 </div>
 </div>
 )}
 </div>
 );
}

export default function Reforme() {
 return (
 <div data-testid="reforme-view" className="space-y-10">
 {/* Hero */}
 <div className="relative overflow-hidden bg-gradient-to-br from-[#15294F] via-[#1E3A6F] to-[#15294F] text-white rounded-2xl shadow-keyto p-7 md:p-10">
 <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#2D6BE0]/20 blur-3xl" />
 <div className="relative">
 <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-white/10 border border-white/20 mb-4">
 <Sparkles size={12} className="text-amber-300" />
 Réforme PCG · Règlement ANC 2022-06
 </span>
 <h1 className="font-heading text-3xl md:text-5xl font-extrabold leading-tight">
 La réforme du PCG{""}
 <span className="text-amber-300">2025</span>
 </h1>
 <p className="mt-4 text-white/80 max-w-3xl text-sm md:text-base leading-relaxed">
 {REFORME_TLDR.texte}
 </p>
 </div>
 </div>

 {/* 5 changements majeurs */}
 <section data-testid="reforme-section-changements">
 <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
 Les 5 changements
 </p>
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] mb-6">
 Ce qui change concrètement
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {REFORME_POINTS.map((p) => (
 <article
 key={p.n}
 data-testid={`reforme-point-${p.n}`}
 className="bg-white border border-keyto rounded-2xl p-5 md:p-6 shadow-keyto hover:shadow-lg transition flex gap-4"
 >
 <div
 className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-mono font-extrabold text-lg"
 style={{
 backgroundColor: `${p.color}15`,
 color: p.color,
 border: `1px solid ${p.color}40`,
 }}
 >
 {p.n}
 </div>
 <div className="flex-1">
 <h3 className="font-heading text-lg font-extrabold text-[#15294F] mb-2 leading-tight">
 {p.titre}
 </h3>
 <p className="text-sm text-slate-600 leading-relaxed">
 {p.desc}
 </p>
 </div>
 </article>
 ))}
 </div>
 </section>

 {/* Table avant/après */}
 <section data-testid="reforme-section-table" className="bg-white border border-keyto rounded-2xl shadow-keyto overflow-hidden">
 <div className="p-6 border-b border-keyto bg-slate-50">
 <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
 Mémo de reclassement
 </p>
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F]">
 Avant → Après : les comptes qui changent
 </h2>
 <p className="mt-2 text-sm text-slate-600">{REFORME_TABLE.intro}</p>
 </div>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-slate-50/50">
 <th className="text-left py-3 px-5 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-keyto">
 Opération
 </th>
 <th className="text-center py-3 px-3 text-[10px] font-bold uppercase tracking-wider text-[#A33B4B] border-b border-keyto w-28">
 Avant
 </th>
 <th className="text-center py-3 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-keyto w-8">
 →
 </th>
 <th className="text-center py-3 px-3 text-[10px] font-bold uppercase tracking-wider text-[#2F7A5C] border-b border-keyto w-32">
 Après
 </th>
 <th className="text-left py-3 px-5 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-keyto w-44">
 Bloc
 </th>
 </tr>
 </thead>
 <tbody>
 {REFORME_TABLE.rows.map((row, i) => {
 const isDeleted = row.apres ==="supprimés";
 return (
 <tr
 key={i}
 className={i % 2 === 0 ?"bg-white" :"bg-slate-50/30"}
 >
 <td className="py-3 px-5 text-sm text-[#15294F]">
 {row.op}
 </td>
 <td className="py-3 px-3 text-center">
 <AccountBadge value={row.avant} variant="old" />
 </td>
 <td className="py-3 px-3 text-center text-slate-300">
 <ArrowRight size={14} className="inline" />
 </td>
 <td className="py-3 px-3 text-center">
 <AccountBadge
 value={row.apres}
 variant={isDeleted ?"deleted" :"new"}
 />
 </td>
 <td className="py-3 px-5 text-xs font-semibold text-slate-600">
 {row.bloc}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 </section>

 {/* Arbre de décision */}
 <section data-testid="reforme-section-arbre">
 <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
 Méthode
 </p>
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] mb-2">
 {REFORME_ARBRE.titre}
 </h2>
 <p className="text-sm text-slate-600 mb-6 max-w-3xl">
 {REFORME_ARBRE.intro}
 </p>

 {/* Tree visualization */}
 <div className="bg-gradient-to-br from-slate-50 to-white border border-keyto rounded-2xl p-6 md:p-8">
 <div className="space-y-4">
 {REFORME_ARBRE.questions.map((q, i) => (
 <div
 key={i}
 className="flex items-start gap-4 bg-white border border-keyto rounded-xl p-4"
 >
 <div className="shrink-0 w-8 h-8 rounded-full bg-[#2D6BE0] text-white text-sm font-bold flex items-center justify-center">
 {i + 1}
 </div>
 <p className="text-sm text-[#15294F] font-medium pt-1 flex-1">
 {q}
 </p>
 </div>
 ))}
 </div>

 {/* Outcomes */}
 <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="bg-emerald-50 border-2 border-[#2F7A5C] rounded-xl p-5">
 <div className="flex items-center gap-2 mb-2">
 <div className="w-7 h-7 rounded-full bg-[#2F7A5C] text-white flex items-center justify-center">
 <Check size={14} />
 </div>
 <span className="text-xs font-bold uppercase tracking-wider text-[#2F7A5C]">
 {REFORME_ARBRE.oui.label}
 </span>
 </div>
 <p className="font-heading text-sm font-extrabold text-[#15294F] mb-1">
 {REFORME_ARBRE.oui.sub}
 </p>
 <p className="text-xs text-slate-600 italic">
 {REFORME_ARBRE.oui.note}
 </p>
 </div>
 <div className="bg-amber-50 border-2 border-[#A56A2E] rounded-xl p-5">
 <div className="flex items-center gap-2 mb-2">
 <div className="w-7 h-7 rounded-full bg-[#A56A2E] text-white flex items-center justify-center">
 <X size={14} />
 </div>
 <span className="text-xs font-bold uppercase tracking-wider text-[#A56A2E]">
 {REFORME_ARBRE.non.label}
 </span>
 </div>
 <p className="font-heading text-sm font-extrabold text-[#15294F] mb-1">
 {REFORME_ARBRE.non.sub}
 </p>
 <p className="text-xs text-slate-600 italic">
 {REFORME_ARBRE.non.note}
 </p>
 </div>
 </div>

 {/* Exceptions */}
 <div className="mt-6 bg-[#15294F] text-white rounded-xl p-5">
 <div className="flex items-start gap-3">
 <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-300" />
 <div>
 <p className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-2">
 {REFORME_ARBRE.apres.titre}
 </p>
 <ul className="space-y-1.5 text-sm leading-relaxed">
 {REFORME_ARBRE.apres.items.map((item, i) => (
 <li key={i} className="flex gap-2">
 <span className="text-amber-300">•</span>
 <span>{item}</span>
 </li>
 ))}
 </ul>
 <p className="mt-3 text-xs text-white/60 italic">
 {REFORME_ARBRE.apres.note}
 </p>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* 3 réflexes */}
 <section data-testid="reforme-section-reflexes">
 <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
 À retenir
 </p>
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] mb-6">
 3 réflexes à corriger
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 {REFORME_REFLEXES.map((r, i) => (
 <article
 key={i}
 data-testid={`reforme-reflexe-${i}`}
 className="bg-white border-l-4 border-[#A33B4B] border-y border-r border-keyto rounded-r-xl p-5"
 >
 <div className="flex items-start gap-2 mb-3">
 <Lightbulb size={16} className="text-[#A33B4B] shrink-0 mt-0.5" />
 <h3 className="font-heading text-sm font-extrabold text-[#15294F] leading-tight">
 {r.titre}
 </h3>
 </div>
 <p className="text-xs text-slate-600 leading-relaxed">{r.desc}</p>
 </article>
 ))}
 </div>
 </section>

 {/* Quiz vérification */}
 <section data-testid="reforme-section-quiz">
 <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
 Vérifiez-vous
 </p>
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] mb-6">
 3 questions pour valider
 </h2>
 <div className="space-y-3">
 {REFORME_QUIZ.map((quiz, i) => (
 <QuizCard key={i} q={quiz.q} r={quiz.r} idx={i} />
 ))}
 </div>
 </section>
 </div>
 );
}
